import { useState, type FormEvent, type ReactNode } from 'react';
import '@/styles/pages.css';
import { MIN_PASSWORD_LENGTH } from '@gdec/shared';
import { api, errorMessage } from '@/api/client';
import { useAuth } from '@/auth/AuthContext';
import { Spinner } from '@/components/Spinner';

/**
 * Set or change your own password.
 *
 * Changing an existing password revokes every session, this one included — so
 * the user is signed out and must sign in again. That is deliberate, and saying
 * so up front is kinder than a silent bounce to the login page afterwards.
 *
 * An account that has never had a password (signed in with an emailed code, or
 * given one by an administrator) is not asked for a current one it does not have.
 */
export function ChangePasswordPage({ forced = false }: { forced?: boolean }): ReactNode {
  const { signOut, user } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < MIN_PASSWORD_LENGTH;
  const blocked = busy || next.length === 0 || mismatch || tooShort || confirm.length === 0;

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.auth.changePassword<{ ok: boolean; signedOut: boolean }>({
        ...(current ? { currentPassword: current } : {}),
        newPassword: next,
      });
      setDone(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="panel">
        <h3>Password changed</h3>
        <p className="hint">
          Every signed-in session was ended, including this one. Sign in again with your new
          password.
        </p>
        <button type="button" className="btn btn-primary" onClick={() => void signOut()}>
          Go to sign in
        </button>
      </div>
    );
  }

  return (
    <div className="panel">
      <h3>{forced ? 'Choose a password' : 'Change password'}</h3>

      {forced ? (
        <p className="hint">
          Your password was set for you, so it has been seen by whoever set it — and it stops
          working within a day. Choose your own before going any further.
        </p>
      ) : null}

      {error ? (
        <div className="form-error" role="alert">
          {error}
        </div>
      ) : null}

      <form onSubmit={onSubmit} className="narrow-form">
        <div className="field">
          <label htmlFor="cp-current">
            {forced ? 'Temporary password' : 'Current password'}{' '}
            <span className="hint-inline">
              {forced
                ? '(the one you were given — it is your current password)'
                : '(leave blank if you have never set one)'}
            </span>
          </label>
          <input
            id="cp-current"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </div>

        <div className="field">
          <label htmlFor="cp-new">New password</label>
          <input
            id="cp-new"
            type="password"
            autoComplete="new-password"
            required
            value={next}
            onChange={(e) => setNext(e.target.value)}
          />
          <p className={tooShort ? 'field-error' : 'hint'}>
            At least {MIN_PASSWORD_LENGTH} characters. Length is what matters — a long phrase beats
            a short one with punctuation in it.
          </p>
        </div>

        <div className="field">
          <label htmlFor="cp-confirm">Confirm new password</label>
          <input
            id="cp-confirm"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
          {mismatch ? <p className="field-error">These do not match.</p> : null}
        </div>

        <button className="btn btn-primary" type="submit" disabled={blocked}>
          {busy ? <Spinner /> : null} Save new password
        </button>
        <p className="hint">
          Signed in as {user?.email}. Saving will sign you out everywhere.
        </p>
      </form>
    </div>
  );
}
